import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack } from '@mui/material';
import { TrainingInsights, DimensionKey } from './types';
import ProcessingSection from './sections/ProcessingSection';
import { SectionAccordionContext, SectionAccordionState } from './sections/SectionAccordionContext';
import renderInsightSection from './renderInsightSection';
import { InsightSectionId, detailSectionsFor, sectionAnchorId } from './insightSections';

interface Props {
  insights: TrainingInsights;
  /** Jump the session's conversation timeline to an ISO timestamp. */
  onNavigateToTimestamp?: (timestampIso: string) => void;
  /** Section to expand and scroll to on open, when arriving from a Summary card. */
  focusSection?: InsightSectionId;
}

/** Long enough for the sections above a focused one to lay out on first paint. */
const OPEN_AFTER_LAYOUT_MS = 150;

const scrollToSection = (id: InsightSectionId): void => {
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  document.getElementById(sectionAnchorId(id))?.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'start',
  });
};

/** Which sections are open. Every section starts closed. */
const useExpandedSections = () => {
  const [expanded, setExpanded] = useState<ReadonlySet<InsightSectionId>>(() => new Set());

  const toggle = useCallback((id: InsightSectionId) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const open = useCallback((id: InsightSectionId) => {
    setExpanded((current) => (current.has(id) ? current : new Set(current).add(id)));
  }, []);

  return { expanded, toggle, open };
};

/**
 * Details sub-tab: one collapsible section per dimension that applies to this
 * kind of session, in the same order as the Summary cards, with a jump-to row
 * on top. Every section starts closed; jumping to one opens it, then scrolls.
 */
const TrainingInsightsDetails: React.FC<Props> = ({ insights, onNavigateToTimestamp, focusSection }) => {
  const isPending = insights?.status === 'pending';
  const isDimensionComplete = (key: DimensionKey): boolean =>
    !isPending || (insights?.dimensionsComplete?.includes(key) ?? false);

  const sections = detailSectionsFor(insights).filter(
    (section) => section.hasData(insights) || !isDimensionComplete(section.dimension),
  );

  const { expanded, toggle, open } = useExpandedSections();
  // A jump to a closed section scrolls only once it has expanded: with every
  // section collapsed the page is often too short to scroll at all.
  const pendingScrollRef = useRef<InsightSectionId | null>(null);

  const jumpTo = useCallback((id: InsightSectionId) => {
    if (expanded.has(id)) {
      scrollToSection(id);
      return;
    }
    pendingScrollRef.current = id;
    open(id);
  }, [expanded, open]);

  const handleExpanded = (id: InsightSectionId) => {
    if (pendingScrollRef.current !== id) return;
    pendingScrollRef.current = null;
    scrollToSection(id);
  };

  useEffect(() => {
    if (!focusSection) return undefined;
    // Let the sections above the target lay out before it expands.
    const timer = window.setTimeout(() => jumpTo(focusSection), OPEN_AFTER_LAYOUT_MS);
    return () => window.clearTimeout(timer);
    // Only a new focus request should re-run this, not every expand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSection]);

  const accordionStateFor = (id: InsightSectionId): SectionAccordionState => ({
    idBase: sectionAnchorId(id),
    isExpanded: expanded.has(id),
    onToggle: () => toggle(id),
    onExpanded: () => handleExpanded(id),
  });

  return (
    <Box>
      {sections.length > 1 && (
        <Stack
          component="nav"
          aria-label="Jump to a section"
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          mb={2}
        >
          {sections.map((section) => (
            <Chip
              key={section.id}
              label={section.title}
              variant="outlined"
              size="small"
              clickable
              onClick={() => jumpTo(section.id)}
            />
          ))}
        </Stack>
      )}

      {sections.map((section) => (
        <Box key={section.id} id={sectionAnchorId(section.id)} sx={{ scrollMarginTop: 16 }}>
          <SectionAccordionContext.Provider value={accordionStateFor(section.id)}>
            {isDimensionComplete(section.dimension)
              ? renderInsightSection(section.id, insights, onNavigateToTimestamp)
              : <ProcessingSection title={section.title} />}
          </SectionAccordionContext.Provider>
        </Box>
      ))}
    </Box>
  );
};

export default TrainingInsightsDetails;
